// wizardConfigs/onboardingConfig.ts
import { WizardConfig } from '../type.js';


/* 
- travel_interests: What they enjoy when traveling (beaches, hiking, food, etc.)
- travel_style: Their travel vibe (adventurous, relaxed, cultural)
- past_trips: Places they've visited that they enjoyed
,
  "travel_style": "string or null",
  "past_trips": [array of strings or empty]

- Their travel style: {{travel_style}}
- Places they've been: {{past_trips}} 

,
  travelStyle: fields.travel_style,
  pastTrips: fields.past_trips || []

*/

export const onboardingWizardConfig: WizardConfig = {
  id: 'onboarding',
  name: 'User Onboarding Wizard',
  fieldDefinitions: [
    { name: 'user_name', required: false, defaultValue: null, description: 'User\'s name (already available in userData)' }, 
    { name: 'travel_interests', required: true, defaultValue: [], description: 'Travel interests (beaches, cities, culture, food, etc.)' },
    { name: 'home_location', required: true, defaultValue: null, description: 'Where the user lives (city/country)' }
    //{ name: 'travel_style', required: true, defaultValue: null, description: 'Travel preferences (adventurous, relaxed, cultural, etc.)' },
    //{ name: 'past_trips', required: false, defaultValue: [], description: 'Places they\'ve visited before' }
  ],
  extractionPrompt: `I'm Trippy, a friendly AI travel companion helping users plan amazing trips while building a natural relationship.

    Extract onboarding information from this conversation in JSON format.
    For the first message: The user's name is already available from userData - don't ask for it. Instead, use the name in a personalized welcome message. For new users, introduce yourself warmly as Trippy with a casual greeting like "Hey [name]! I'm Trippy, your AI travel buddy." For returning users, use "Welcome back, [name]!" Focus on asking about their travel style and interests.

    In all messages: ALWAYS address the user by their name. Use phrases like "So [name], tell me about..." or "That's awesome, [name]!" to maintain a personal connection.

    Look for details about:
    
    - home_location: Where they're from or currently live
    
    Output in JSON format:
    {
    "user_name": "string (from userData, don't ask for it)",
    "travel_interests": [array of strings or empty],
    "home_location": "string or null"
    }

    After the JSON, continue the conversation naturally with a casual follow-up that feels like texting with a friend.`,
  
  completionPrompt: `I'm Trippy, a friendly AI travel companion.

    Create a warm wrap-up message for {{user_name}} that establishes our relationship as travel buddies.

    Include these details naturally:
    - They're from: {{home_location}}
    - They enjoy: {{travel_interests}}


    REQUIREMENTS:
    1. Use casual, friendly language like a real person texting
    2. Address them by name
    3. Keep it under 75 words
    4. Mention 1-2 specific preferences they shared
    5. End with "What can I help you explore today?" to transition to the main chat`,
    
    formatFunc: (fields) => ({
        type: "userPreferences",
        data: {
        name: fields.user_name,
        homeLocation: fields.home_location,
        preferences: {
            interests: fields.travel_interests || []
        },
        createdAt: new Date().toISOString()
        }
    })
    };

export const onboardingWizardConfig_UNUSED: WizardConfig = {
  id: 'onboarding',
  name: 'User Onboarding Wizard',
  fieldDefinitions: [
    { name: 'user_name', required: true, defaultValue: null, description: 'User\'s name or nickname' },
    { name: 'travel_interests', required: true, defaultValue: [], description: 'Travel interests (beaches, cities, culture, food, etc.)' },
    { name: 'experience_level', required: true, defaultValue: null, description: 'Travel experience (novice, intermediate, experienced)' },
    { name: 'bucket_list', required: false, defaultValue: [], description: 'Places they want to visit' },
    { name: 'travel_style', required: true, defaultValue: null, description: 'Travel preferences (luxury, budget, adventure, etc.)' }
  ],
  extractionPrompt: `I'm Trippy, a friendly AI travel companion helping users plan amazing trips while building a natural relationship. 
    Extract onboarding information from this conversation in JSON format.

    For the first message: Introduce yourself warmly as Trippy with a casual, upbeat greeting like "Hey there! I'm Trippy, your AI travel buddy." Ask for their name and an open-ended question about travel preferences in a conversational way.

    In subsequent messages: ALWAYS address the user by their name once you know it. Use phrases like "So [name], tell me more about..." or "That's awesome, [name]! I'd love to know..." to create a personal connection.

    Example good first exchanges:
    - "Hey there! I'm Trippy, your personal travel buddy. What's your name and what kind of traveler are you?"
    - "Welcome to Tripper! I'm Trippy and I'm excited to help plan your next adventure. What should I call you and what places are on your bucket list?"

    For all messages, look for details about:
    - user_name: How they'd like to be called
    - travel_interests: What they enjoy when traveling (beaches, hiking, food, etc.)
    - home_location: Where they're from or currently live
    - travel_style: Their travel vibe (adventurous, relaxed, cultural)
    - past_trips: Places they've visited that they enjoyed

    Output in JSON format:
    {
    "user_name": "string or null",
    "travel_interests": [array of strings or empty],
    "home_location": "string or null",
    "travel_style": "string or null",
    "past_trips": [array of strings or empty]
    }

    After the JSON, include a natural, conversational follow-up question that feels like a real travel friend texting - short, casual, and focused on learning more about their travel preferences.`,
  
  completionPrompt: `Create a warm, personalized welcome message for {{user_name}} based on their preferences:
  - Interests: {{travel_interests}}
  - Experience: {{experience_level}}
  - Bucket list: {{bucket_list}}
  - Travel style: {{travel_style}}
  
  REQUIREMENTS:
  1. Keep it under 100 words
  2. Be warm and enthusiastic
  3. Mention 1-2 specific preferences they shared
  4. Explain how you'll help plan their future travels
  5. End with a simple travel-related question to start the conversation`,
  
  formatFunc: (fields) => ({
    type: "userPreferences",
    data: {
      name: fields.user_name,
      preferences: {
        interests: fields.travel_interests || [],
        experienceLevel: fields.experience_level,
        bucketList: fields.bucket_list || [],
        travelStyle: fields.travel_style
      },
      createdAt: new Date().toISOString()
    }
  })
};